import {
  NumberOperator,
  RelativeDirection,
  TraitType,
  ValueType,
  isAggregationTraitConfig,
  isCountDedupedTraitConfig,
  isInlineTraitColumn,
  isRelatedJourneyEventColumn,
  isNumberRangeType,
  isOrderDedupedTraitConfig,
  isRelatedColumn,
  isColumnTypeValue,
  JourneySetCondition,
  isTimestampGranularityTimeType,
  TimestampGranularity,
  isJsonPathColumn,
  isTraitColumn,
  getPropertyType,
} from "@hightouch/lib/query/visual/types";
import { JOURNEY_MEMBER_STATE_TIMESTAMP_OPERATORS } from "@hightouch/lib/query/visual/types/operator";
import { JOURNEY_MEMBER_STATE_WINDOW_UNITS } from "@hightouch/lib/query/visual/types/interval";
import { isPresent } from "ts-extras";

import {
  isRealtimeTrait,
  isTraitSupportedInRtaConditions,
} from "src/components/traits/utils";

import {
  EventCondition,
  FunnelCondition,
  NumberOfCondition,
  PropertyCondition,
  ReferencedPropertyCondition,
  SegmentSetCondition,
  TimestampOperator,
  ColumnType,
  TimeType,
  Window,
  TimeRangeValue,
  IntervalValue,
  isJourneyMemberStateColumn,
  OperatorsWithoutValue,
  ColumnComparisonOperatorsByColumnType,
  WithinOperators,
  isPropertyCondition,
} from "src/types/visual";
import { getIntervalInMs } from "src/utils/time";
import { isAndOrCondition } from "src/components/explore/utils/type-guards";
import { RTA_MEMBERSHIP_SNAPSHOTTING_DISABLED_MESSAGE } from "src/components/audiences/rta-membership-reference";

type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};
type ValidationResult<TCondition> = PartialRecord<
  keyof TCondition,
  string | null
>;

type ReferencedBatchAudience = {
  isRealtimeAudience: false;
  parentSnapshottingEnabled: boolean;
};

type ReferencedRealtimeAudience = {
  isRealtimeAudience: true;
  parentSnapshottingEnabled: boolean;
};

export type ValidationTraitDefinition = Parameters<
  typeof isTraitSupportedInRtaConditions
>[0];

type Validator<TCondition> = (
  condition: TCondition,
  options?: {
    ignoreValue?: boolean;
    isRealtimeEvent?: boolean;
    isRealtimeAudience?: boolean;
    /**
     * When set, event-count thresholds above this value are invalid.
     * Realtime audiences cap at REALTIME_EVENT_COUNT_MAX.
     */
    maxEventCount?: number;
    /**
     * The parent model's trait definitions (unfiltered), keyed by id. Enables
     * trait-reference validation for realtime audiences; callers that omit it
     * skip that check.
     */
    traitsById?: ReadonlyMap<string, ValidationTraitDefinition>;
    realtimeTraitsInRtaConditionsEnabled?: boolean;
  } & (
    | {
        referencedAudience?: undefined;
        rtaWritebackReadsIsEnabled?: boolean;
      }
    | {
        referencedAudience?: ReferencedBatchAudience;
        rtaWritebackReadsIsEnabled?: false;
      }
    | {
        referencedAudience: ReferencedRealtimeAudience;
        rtaWritebackReadsIsEnabled: true;
      }
  ),
) => ValidationResult<TCondition>;

export const requiredErrorMessage = "This field is required";
export const numberRequiredErrorMessage = "This field must contain a number";
export const validNumberMessage = "Please enter a valid number";
export const validTimeTypeMessage = "Please select a valid time type";
export const validDateRangeMessage = "Please enter a valid date range";
export const requiredTimeWindowMessage = "Time window is required";
export const getValidMinimumDateMessage = (minimumDate: Date) => {
  const now = new Date();
  const diffInMs = now.getTime() - minimumDate.getTime();
  const diffInHours = Math.round(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInHours <= 24) {
    return `Time window must be within the last 24 hours`;
  } else if (diffInDays === 1) {
    return `Time window must be within the last 1 day`;
  } else {
    return `Time window must be within the last ${diffInDays} days`;
  }
};
export const validDateRangeOrderMessage =
  "Starting value must be before ending value";
export const distinctDateRangeMessage =
  "Start and end values must be different";
export const validWithinQuantityMessage = "Quantity must be greater than 0";
export const unsupportedByRealtimeAudienceMessage =
  "This condition is not supported by realtime audiences";
export const REALTIME_EVENT_COUNT_MAX = 255;
export const realtimeEventCountCapHint = "Thresholds are capped at 255";
export const realtimeEventCountCapMessage = "Event count must be 255 or fewer";
export const traitNotFoundMessage =
  "This trait could not be found on the audience's parent model";
export const unsupportedMemberStateFieldMessage =
  "Member state only supports Journey entry time";
export const unsupportedMemberStateOperatorMessage =
  "This operator is not supported for Journey entry time";
export const memberStateFullTimestampMessage =
  "Member state requires a full timestamp comparison";
export const memberStateValueTypeMessage =
  "Member state requires a timestamp or relative interval";
export const memberStateWindowUnitMessage =
  "Within and not within support only minute, hour, and day intervals";
export const memberStateQuantityMessage =
  "Quantity must be a whole number greater than 0";

const isIntervalValue = (
  value: IntervalValue | TimeRangeValue | string,
): value is IntervalValue => {
  return typeof value !== "string" && "interval" in value;
};

const isTimeRangeValue = (
  value: TimeRangeValue | IntervalValue | string,
): value is TimeRangeValue => {
  return typeof value !== "string" && "after" in value && "before" in value;
};

const checkIfDateRangeRequired = (
  value: string | TimeRangeValue | IntervalValue | null,
  isRequired: boolean,
) => {
  if (isRequired && value == null) {
    return requiredTimeWindowMessage;
  }

  if (value === undefined) {
    return validDateRangeMessage;
  }

  return null;
};

const validateIntervalValue = (value: IntervalValue) => {
  if (!isPresent(value.quantity) || isNaN(value.quantity)) {
    return validNumberMessage;
  }

  return null;
};

const validateAbsoluteDateRange = (
  value: TimeRangeValue,
  inclusive: boolean,
) => {
  if (typeof value.after !== "string" || typeof value.before !== "string") {
    return validDateRangeMessage;
  }

  // The bounds are accepted in either order: the backend sorts them
  // internally and the UI displays them chronologically regardless of which
  // field holds the earlier value. An exclusive range with equal bounds
  // matches nothing, so that is rejected.
  if (
    !inclusive &&
    new Date(value.before).getTime() === new Date(value.after).getTime()
  ) {
    return distinctDateRangeMessage;
  }

  return null;
};

const validateRelativeDateRange = (
  value: TimeRangeValue,
  granularity: TimestampGranularity | undefined,
  inclusive: boolean,
) => {
  if (granularity === TimestampGranularity.Time) {
    if (typeof value.after !== "string" || typeof value.before !== "string") {
      return validDateRangeMessage;
    }

    const before = new Date(value.before);
    const after = new Date(value.after);

    // Unlike absolute date ranges (bounds accepted in either order and sorted
    // chronologically for display), relative ranges need validate bound order.
    // The backend compiles a flipped range to the same window (it sorts the
    // bounds), so this is a guardrail rather than a correctness requirement:
    // For time-of-day ranges specifically, a flipped pair (e.g. 10 PM–2 AM)
    // also reads as an overnight window wrapping midnight, which the query
    // engine cannot express — it would be silently normalized to the
    // complementary daytime window (2 AM–10 PM).
    if (
      inclusive
        ? before.getTime() > after.getTime()
        : before.getTime() >= after.getTime()
    ) {
      return validDateRangeOrderMessage;
    }
  }

  if (
    typeof value === "string" ||
    typeof value.after === "string" ||
    typeof value.before === "string"
  ) {
    return null;
  }

  if (!isPresent(value.after?.quantity) || !isPresent(value.before?.quantity)) {
    return validDateRangeMessage;
  }

  if (value.after.direction === value.before.direction) {
    if (
      inclusive
        ? value.after.quantity < value.before.quantity
        : value.after.quantity <= value.before.quantity
    ) {
      return validDateRangeOrderMessage;
    }
  } else if (
    value.before.direction === RelativeDirection.Forward &&
    value.after.direction === RelativeDirection.Backward
  ) {
    return validDateRangeOrderMessage;
  }

  return !isPresent(value?.after?.quantity) ||
    isNaN(value?.after?.quantity) ||
    !isPresent(value?.before?.quantity) ||
    isNaN(value?.before?.quantity)
    ? validDateRangeMessage
    : null;
};

const validateDatesAgainstMinimum = (
  value: IntervalValue | null,
  minimumDate: Date,
) => {
  // NOTE: the only validation that's supported here is for the 'within' operator, which is a single value.
  if (!value || !minimumDate) {
    return null;
  }

  const now = new Date();

  // Handle interval values (e.g., "last 5 days")
  if (isIntervalValue(value)) {
    if (value.quantity && value.interval) {
      const intervalMs = getIntervalInMs(value.interval);
      const startTime = now.getTime() - value.quantity * intervalMs;

      if (startTime < minimumDate.getTime()) {
        return getValidMinimumDateMessage(minimumDate);
      }
    }
    return null;
  }

  // TODO(XXX): Extend validation to support other operators once needed.
  return null;
};

type ValidateDateRangeArguments = {
  timeType: TimeType | undefined;
  value: string | TimeRangeValue | IntervalValue | null;
  granularity?: TimestampGranularity;
  inclusive?: boolean;
  minimumDate?: Date;
  isRequired?: boolean;
};
export const validateDateRange = ({
  timeType,
  value,
  granularity,
  inclusive = false,
  minimumDate,
  isRequired = false,
}: ValidateDateRangeArguments) => {
  if (timeType === undefined || typeof value === "string") {
    return {};
  }

  // Check if the date range exists and is required
  const requiredError = checkIfDateRangeRequired(value, isRequired);

  if (requiredError) {
    return {
      value: requiredError,
    };
  }

  if (value && isIntervalValue(value)) {
    const intervalError = validateIntervalValue(value);

    if (intervalError) {
      return {
        quantity: intervalError,
      };
    }
  } else if (timeType === TimeType.Absolute) {
    if (value == null || !isTimeRangeValue(value)) {
      return { value: validDateRangeMessage };
    }

    const absoluteDateRangeError = validateAbsoluteDateRange(value, inclusive);

    if (absoluteDateRangeError) {
      return {
        value: absoluteDateRangeError,
      };
    }
  } else if (timeType === TimeType.Relative) {
    if (value == null || !isTimeRangeValue(value)) {
      return { value: validDateRangeMessage };
    }

    const relativeDateRangeError = validateRelativeDateRange(
      value,
      granularity,
      inclusive,
    );

    if (relativeDateRangeError) {
      return {
        value: relativeDateRangeError,
      };
    }
  }

  if (minimumDate && value && isIntervalValue(value)) {
    const minimumDateError = validateDatesAgainstMinimum(value, minimumDate);

    if (minimumDateError) {
      return { value: minimumDateError };
    }
  }

  return {};
};

const valueHasQuantity = (value: unknown): value is { quantity: number } =>
  value != null &&
  typeof value === "object" &&
  "quantity" in value &&
  typeof value.quantity === "number" &&
  !isNaN(value.quantity);

export const validatePropertyCondition: Validator<PropertyCondition> = (
  condition,
  opts,
) => {
  const { property, timeType, operator, value, propertyOptions } = condition;
  const propertyType = getPropertyType(condition);
  const granularity = propertyOptions?.timestampGranularity;

  if (!property) {
    return {
      property: !property ? requiredErrorMessage : null,
    };
  }

  if (isRelatedJourneyEventColumn(property)) {
    if (!property.column.name) {
      return {
        property: "Event property is required",
      };
    }
  }

  if (isJsonPathColumn(property)) {
    if (opts?.isRealtimeAudience) {
      // JSON column paths are not supported by realtime audiences
      return { property: unsupportedByRealtimeAudienceMessage };
    }
    if (!property.path) {
      return { property: requiredErrorMessage };
    }
  }

  if (isJourneyMemberStateColumn(property)) {
    if (
      property.field !== "entered_at" ||
      propertyType !== ColumnType.Timestamp
    ) {
      return { property: unsupportedMemberStateFieldMessage };
    }
    if (opts?.isRealtimeAudience) {
      return { property: unsupportedByRealtimeAudienceMessage };
    }
    const isSupportedTimestampOperator = Object.hasOwn(
      JOURNEY_MEMBER_STATE_TIMESTAMP_OPERATORS,
      operator,
    );
    if (!isSupportedTimestampOperator) {
      return { operator: unsupportedMemberStateOperatorMessage };
    }
    if (granularity != null && granularity !== TimestampGranularity.Full) {
      return { value: memberStateFullTimestampMessage };
    }
    if (isColumnTypeValue(value)) {
      return { value: memberStateValueTypeMessage };
    }
    const bounds = value as Partial<TimeRangeValue> | null | undefined;
    const intervals = [value, bounds?.before, bounds?.after].filter(
      (candidate): candidate is IntervalValue =>
        typeof candidate === "object" &&
        candidate != null &&
        "interval" in candidate,
    );
    const hasUnsupportedWindowUnit =
      WithinOperators.includes(operator) &&
      intervals.some(
        (interval) =>
          !JOURNEY_MEMBER_STATE_WINDOW_UNITS.includes(interval.interval),
      );
    if (hasUnsupportedWindowUnit) {
      return { value: memberStateWindowUnitMessage };
    }
    const hasUnsupportedQuantity = intervals.some(
      (interval) =>
        typeof interval.quantity !== "number" ||
        !Number.isSafeInteger(interval.quantity) ||
        interval.quantity <= 0,
    );
    if (hasUnsupportedQuantity) {
      return { value: memberStateQuantityMessage };
    }
  }

  // Must stay above the OperatorsWithoutValue early return, which would
  // otherwise skip this check for "is not set" conditions.
  if (
    opts?.isRealtimeAudience &&
    opts.traitsById &&
    isRelatedColumn(property) &&
    isTraitColumn(property.column)
  ) {
    const trait = opts.traitsById.get(property.column.traitDefinitionId);
    if (!trait) {
      return { property: traitNotFoundMessage };
    }
    if (
      (isRealtimeTrait(trait) && !opts.realtimeTraitsInRtaConditionsEnabled) ||
      !isTraitSupportedInRtaConditions(trait)
    ) {
      return { condition: unsupportedByRealtimeAudienceMessage };
    }
  }

  if (propertyType === ColumnType.JsonArrayObjects) {
    const group = condition.subconditions?.[0];
    if (!isAndOrCondition(group) || group.conditions.length === 0) {
      return { condition: "Add at least one item condition" };
    }
    if (
      group.conditions.some(
        (child) =>
          !isPropertyCondition(child) ||
          getPropertyType(child) === ColumnType.JsonArrayObjects ||
          isJsonPathColumn(child.property),
      )
    ) {
      return {
        condition: "Item conditions must use fields of objects in this array",
      };
    }
    return {};
  }

  if (OperatorsWithoutValue.includes(operator)) {
    return {};
  }

  if (isColumnTypeValue(value)) {
    if (isRelatedJourneyEventColumn(value.property)) {
      return !value.property.column.name
        ? {
            value: "Event property is required",
          }
        : {};
    }
    if (propertyType && ColumnComparisonOperatorsByColumnType[propertyType]) {
      if (!value?.property) {
        return { value: requiredErrorMessage };
      }
      return {};
    }
  }

  if (propertyType === ColumnType.Timestamp) {
    // 'between' works differently than the rest of the value types
    switch (operator) {
      case TimestampOperator.Between:
      case TimestampOperator.BetweenInclusive:
        return validateDateRange({
          timeType: timeType || TimeType.Relative,
          value,
          granularity,
          inclusive: operator === TimestampOperator.BetweenInclusive,
        });
      case TimestampOperator.Equals:
      case TimestampOperator.Before:
      case TimestampOperator.BeforeInclusive:
      case TimestampOperator.After:
      case TimestampOperator.AfterInclusive:
      case TimestampOperator.Within:
      case TimestampOperator.NotWithin:
        if (timeType === TimeType.Absolute) {
          return {
            value:
              typeof value !== "string" ? numberRequiredErrorMessage : null,
          };
        } else if (timeType === TimeType.Relative) {
          if (isTimestampGranularityTimeType(granularity)) {
            if (Array.isArray(value)) {
              return {
                value: value.length === 0 ? requiredErrorMessage : null,
              };
            }
            return {
              value:
                typeof value !== "string" ? numberRequiredErrorMessage : null,
            };
          }
          if (!valueHasQuantity(value)) {
            return { value: numberRequiredErrorMessage };
          }
          if (
            WithinOperators.includes(operator) &&
            granularity &&
            granularity !== TimestampGranularity.Full
          ) {
            // There is a bug with non-full granularity `within` queries where quantity = 0.
            // It can be queried instead with `equals` or `between` operators.
            // https://carryinternal.slack.com/archives/C03U5T2BU0Y/p1749587530124219?thread_ts=1749497466.811039&cid=C03U5T2BU0Y
            if (value.quantity <= 0) {
              return { value: validWithinQuantityMessage };
            }
          }
          return {};
        } else if (value?.type === ValueType.Column) {
          return {
            value:
              value?.property == null ? "Attribute column is required." : null,
          };
        }
        break;
      case TimestampOperator.Exists:
      case TimestampOperator.DoesNotExist:
      case TimestampOperator.Anniversary:
      default:
        break;
    }
  }

  if (
    propertyType === ColumnType.Number ||
    propertyType === ColumnType.BigInt
  ) {
    if (
      isRelatedColumn(property) &&
      propertyOptions?.traitType === "trait" &&
      property.column.type === "trait" &&
      isNumberRangeType(value)
    ) {
      // There is currently not a direct way to check if it's a predictive trait, you need to look up the trait definition,
      // so we've carved out an exception here for predictive traits.
      return {};
    }

    if (operator === NumberOperator.Between) {
      return {
        value: isNumberRangeType(value)
          ? null
          : "Value must be a valid range of numbers",
      };
    }

    // Validate single values
    if (!Array.isArray(value)) {
      if (value != null && (value === "" || !Number.isFinite(Number(value)))) {
        return {
          value: "Value must be numeric",
        };
      }
    }

    // Validate array elements
    if (Array.isArray(value)) {
      for (const v of value) {
        if (typeof v === "string") {
          const trimmed = v.trim();
          if (trimmed === "" || isNaN(Number(trimmed))) {
            return {
              value: `Cannot use non-numeric value: "${v}"`,
            };
          }
        }
        // Also validate that numbers are finite
        if (typeof v === "number" && !Number.isFinite(v)) {
          return {
            value: "Cannot use Infinity or NaN in numeric condition",
          };
        }
      }
    }

    if (propertyOptions?.percentile) {
      return {
        value:
          value < 0 || value > 100
            ? "Percentile value must be between 0 and 100"
            : null,
      };
    }
  }

  // Check if property is a custom trait and validate it
  if (isRelatedColumn(property) && isInlineTraitColumn(property.column)) {
    const { traitType, traitConfig, relationshipId } = property.column;

    if (opts?.isRealtimeAudience) {
      return { condition: unsupportedByRealtimeAudienceMessage };
    }

    if (!traitType) {
      return { property: "Trait aggregation type is required" };
    }

    // Narrowing with `isInlineTraitColumn` does not work correctly.
    // Makes typescript think `TraitType.Formula` is not assignable to `traitType`
    if ((traitType as TraitType) === TraitType.Formula && !relationshipId) {
      return { property: "Related model or event is required" };
    }

    if (isAggregationTraitConfig(traitType, traitConfig)) {
      if (!traitConfig.column) {
        return { property: "Column is required" };
      }
    }

    if (isCountDedupedTraitConfig(traitType, traitConfig)) {
      if (!traitConfig.toSelect) {
        return { property: "Column is required" };
      }
    }

    if (isOrderDedupedTraitConfig(traitType, traitConfig)) {
      if (!traitConfig.toSelect || !traitConfig.orderBy) {
        return {
          property: "Column is required",
        };
      }
    }
  }

  if (value?.type === ValueType.Column) {
    if (!value?.property) {
      return { value: requiredErrorMessage };
    }
  }

  if (operator == null || value == null) {
    return {
      property: null,
      operator: operator == null ? requiredErrorMessage : null,
      value: value == null ? requiredErrorMessage : null,
    };
  }

  return {};
};

export const validateReferencePropertyFilter: Validator<
  ReferencedPropertyCondition
> = ({ property, valueFromColumn }) => {
  if (!property) {
    return {
      property: property == null ? requiredErrorMessage : null,
    };
  }

  if (!valueFromColumn) {
    return {
      property: null,
      valueFromColumn: valueFromColumn == null ? requiredErrorMessage : null,
    };
  }

  return {};
};

export const validateEventCondition: Validator<EventCondition> = (
  { eventModelId, value, window },
  options = {
    ignoreValue: false,
    isRealtimeEvent: false,
    isRealtimeAudience: false,
  },
) => {
  if (options?.isRealtimeEvent && !window) {
    return { window: requiredTimeWindowMessage };
  }

  let valueError: string | null = null;
  if (value == null && !options?.ignoreValue) {
    valueError = requiredErrorMessage;
  } else if (
    options?.maxEventCount != null &&
    value != null &&
    Number(value) > options.maxEventCount
  ) {
    valueError = realtimeEventCountCapMessage;
  }

  return {
    condition: options.isRealtimeAudience
      ? unsupportedByRealtimeAudienceMessage
      : null,
    eventModelId: eventModelId == null ? requiredErrorMessage : null,
    value: valueError,
  };
};

export const validateNumberOfCondition: Validator<NumberOfCondition> = (
  { relationshipId, value },
  opts,
) => ({
  condition: opts?.isRealtimeAudience
    ? unsupportedByRealtimeAudienceMessage
    : null,
  relationshipId: relationshipId == null ? requiredErrorMessage : null,
  value: value == null ? requiredErrorMessage : null,
});

export const validateFunnelCondition: Validator<FunnelCondition> = ({
  eventModelId,
}) => {
  return { eventModelId: eventModelId != null ? null : requiredErrorMessage };
};

export const validateSegmentSetCondition: Validator<SegmentSetCondition> = (
  { modelId, includes },
  opts,
) => {
  if (opts?.isRealtimeAudience) {
    return {
      condition: unsupportedByRealtimeAudienceMessage,
      modelId: modelId == null ? requiredErrorMessage : null,
      includes: typeof includes !== "boolean" ? requiredErrorMessage : null,
    };
  }

  const referencedAudience = opts?.referencedAudience;
  if (referencedAudience?.isRealtimeAudience) {
    if (!referencedAudience.parentSnapshottingEnabled) {
      return {
        modelId: RTA_MEMBERSHIP_SNAPSHOTTING_DISABLED_MESSAGE,
        includes: typeof includes !== "boolean" ? requiredErrorMessage : null,
        condition: null,
      };
    }
  }

  return {
    condition: null,
    modelId: modelId == null ? requiredErrorMessage : null,
    includes: typeof includes !== "boolean" ? requiredErrorMessage : null,
  };
};

export const validateJourneySetCondition: Validator<JourneySetCondition> = (
  { journeyIds, filter },
  opts,
) => ({
  condition: opts?.isRealtimeAudience
    ? unsupportedByRealtimeAudienceMessage
    : null,
  journeyIds: journeyIds.length === 0 ? requiredErrorMessage : null,
  filter:
    filter === null || filter === undefined
      ? requiredErrorMessage
      : filter.condition === null || filter.condition === undefined
        ? requiredErrorMessage
        : null,
});

export const validateWindowCondition: Validator<Window | null | undefined> = (
  window,
  { isRealtimeEvent = false } = {},
) => {
  if (window === null || window === undefined) {
    return {};
  }

  return validateDateRange({
    timeType: window.timeType,
    value: window.value,
    granularity: window.propertyOptions?.timestampGranularity,
    inclusive: window.operator === TimestampOperator.BetweenInclusive,
    // realtime event conditions _must_ have a time window,
    isRequired: isRealtimeEvent,
    // the window may not be longer than 24 hours in the past.
    minimumDate: isRealtimeEvent
      ? new Date(Date.now() - getIntervalInMs("day"))
      : undefined,
  });
};
